import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription, finalize, takeUntil } from "rxjs";
import { IOrganizationAccess } from "src/app/shared/interfaces/access.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConnectionEventsService } from "src/app/shared/services/connection-events.service";
import { SessionService } from "src/app/shared/services/session.service";
import { HandleLinkService } from "../../domain/services/handle-link.service";

enum LinkStep {
  Loading = "loading",
  Error = "error",
}

@Component({
  selector: "app-link-page",
  templateUrl: "./link-page.component.html",
  styleUrls: ["./link-page.component.scss"],
})
export class LinkPageComponent implements OnInit, OnDestroy {
  public readonly LinkStep = LinkStep;
  public step: LinkStep = LinkStep.Loading;
  public linkExpired = false;
  private linkSubscription?: Subscription;
  private onDestroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private handleLinkService: HandleLinkService,
    private connectionEventsService: ConnectionEventsService,
    private apiService: ApiService,
    private translateService: TranslateService
  ) {}

  /**
   * Clears any existing session, switches to the language given in the query params (if any)
   * and processes the link token found there.
   */
  ngOnInit(): void {
    // Disconnect any user that was connected before handling this link - otherwise a
    // stale/expired session could trigger the login dialog while this page is still loading.
    this.sessionService.clear();

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy$)).subscribe((queryParams) => {
      const token = queryParams.get("token");
      const lang = queryParams.get("lang");

      // Show the page in the same language as the email the link comes from
      if (lang) {
        this.sessionService.userLang = lang;
        this.translateService.use(lang);
      }

      if (!token) {
        this.router.navigate(["/not-found"]);
        return;
      }

      this.processLink(token);
    });
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Processes the incoming link and handles authentication and redirection.
   * @param token The token carried by the link
   */
  private processLink(token: string): void {
    // Cancel any previous handle-link call still in flight, so it can't resume token
    // verification right after this new call has just suspended it again.
    this.linkSubscription?.unsubscribe();

    this.step = LinkStep.Loading;
    this.linkExpired = false;

    /**
     * Token verification must be suspended while this call is in flight: the session isn't
     * set up yet, and a parallel refresh attempt could otherwise race with the link being handled.
     */
    this.apiService.suspendVerifyTokensFlow();

    this.linkSubscription = this.handleLinkService
      .handleLink(token)
      .pipe(
        takeUntil(this.onDestroy$),
        // Guarantees resumeVerifyTokensFlow() runs even if this subscription is torn down
        // before the response arrives
        finalize(() => this.apiService.resumeVerifyTokensFlow())
      )
      .subscribe({
        next: (value: IOrganizationAccess) => {
          if (!value.tokens.accessToken || !value.tokens.refreshToken) {
            this.step = LinkStep.Error;
            return;
          }

          this.apiService.resumeVerifyTokensFlow();
          this.translateService.use(value.language);
          this.connectionEventsService.notifyLogin(value);
          this.router.navigate(["/profile"], { state: { changePassword: true } });
        },
        error: (err) => {
          this.apiService.resumeVerifyTokensFlow();
          this.linkExpired = err?.status === 410;
          this.step = LinkStep.Error;
        },
      });
  }
}
